import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { ControlNumbersStatusService, FiscalAccessService } from '@common/services';
import { FreeRequestStatus } from '@common/services/control-numbers.service';
import { ImproveFinaAccessService } from '@common/services/improve-fina-access.service';
import { Observable, of, Subject } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, startWith, switchMap, takeUntil } from 'rxjs/operators';

/** Rutas del POS (`app-routing.module.ts`): la alerta solo se ve ahí. */
export const isPosUrl = (url: string | null | undefined): boolean => {
    const path = String(url || '').split(/[?#]/)[0];
    return path === '/pos' || path.startsWith('/pos/');
};

/**
 * Alerta de pocos números de control en el POS (ADM-827). Va en flujo, encima del contenido de
 * `NavComponent`, para no competir con el banner fijo de renovación. Solo se ve bajo `/pos`, con
 * la solicitud gratuita activa y el disponible en o bajo el umbral que manda api-admin.
 *
 * La compuerta sin red es `FiscalAccessService.hasFiscalAccess$()` (reactiva: el bundle puede
 * llegar después de entrar al POS). Con ella abierta se pide el estado una vez por entrada al
 * POS; la integración Unidigital la dice `hasIntegration` de la respuesta. Un GET fallido no
 * muestra nada ni avisa.
 */
@Component({
    selector: 'fina-control-numbers-alert-banner',
    templateUrl: './control-numbers-alert-banner.component.html',
    styleUrls: ['./control-numbers-alert-banner.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class ControlNumbersAlertBannerComponent implements OnInit, OnDestroy {
    private readonly destroy$ = new Subject<void>();

    /** Bajo `/pos` y con acceso fiscal del comercio. */
    gateOpen = false;
    status: FreeRequestStatus | null = null;

    constructor(
        private readonly router: Router,
        private readonly fiscalAccess: FiscalAccessService,
        private readonly controlNumbersStatus: ControlNumbersStatusService,
        private readonly improveFinaAccess: ImproveFinaAccessService,
    ) {}

    ngOnInit(): void {
        this.controlNumbersStatus.status$.pipe(takeUntil(this.destroy$)).subscribe(status => (this.status = status));

        this.gate$()
            .pipe(
                switchMap(open => {
                    this.gateOpen = open;
                    return open ? this.controlNumbersStatus.load$().pipe(catchError(() => of(null))) : of(null);
                }),
                takeUntil(this.destroy$),
            )
            .subscribe();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    get visible(): boolean {
        const status = this.status;
        return (
            this.gateOpen &&
            !!status &&
            status.hasIntegration === true &&
            status.fiscalPlan === true &&
            status.freeMode === true &&
            !!status.package &&
            Number(status.available) <= Number(status.threshold)
        );
    }

    /**
     * El enlace a «Mejora tu Fina» lo ve el dueño del comercio (o una sesión de soporte, en lectura): la
     * página es solo del dueño desde el 2026-11-03 y el api responde 403 al POST de un asociado. El resto ve
     * el aviso de pedírselo al dueño.
     */
    get canRequest(): boolean {
        return this.improveFinaAccess.canAccessSync();
    }

    get remainingLabel(): string {
        const available = Math.max(0, Number(this.status?.available) || 0);
        return available === 1 ? 'Te queda 1 número de control.' : `Te quedan ${available} números de control.`;
    }

    private gate$(): Observable<boolean> {
        const onPos$ = this.router.events.pipe(
            filter((event): event is NavigationEnd => event instanceof NavigationEnd),
            map(event => event.urlAfterRedirects),
            startWith(this.router.url),
            map(url => isPosUrl(url)),
            distinctUntilChanged(),
        );
        return onPos$.pipe(
            switchMap(onPos => (onPos ? this.fiscalAccess.hasFiscalAccess$() : of(false))),
            distinctUntilChanged(),
        );
    }
}
